<script setup lang="ts">
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { formatDistance, type PickupPoint } from '../lib/deliveryOptions';

/**
 * OpenStreetMap tiles rather than Google Maps: a Google map needs an API key with
 * billing attached, which cannot be shipped in a public bundle. Each location also
 * links out to Google Maps, which needs no key. Tile requests do tell
 * tile.openstreetmap.org roughly which area is being looked at — noted in
 * docs/data-boundary.md.
 */

const props = defineProps<{
  points: PickupPoint[];
  /**
   * The subset the map should frame. Fitting all 20 locations zooms out to half the
   * province and turns the nearby ones into a single blob, so the markers cover
   * everything while the viewport follows the list the reader is actually looking at.
   */
  focus: PickupPoint[];
  selected: number | null;
}>();
const emit = defineEmits<{ select: [index: number] }>();

const container = ref<HTMLDivElement | null>(null);
let map: L.Map | null = null;
let markers: L.Marker[] = [];

/** An inline icon, so no image assets have to resolve through the bundler. */
function icon(active: boolean): L.DivIcon {
  return L.divIcon({
    className: 'pickup-pin-wrap',
    html: `<span class="pickup-pin${active ? ' pickup-pin--active' : ''}"></span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

function draw() {
  if (!map) {
    return;
  }

  for (const marker of markers) {
    marker.remove();
  }
  markers = [];

  const usable = props.points.filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  if (usable.length === 0) {
    return;
  }

  usable.forEach((point, index) => {
    const marker = L.marker([point.lat, point.lng], {
      icon: icon(false),
      keyboard: true,
      title: `${point.name} — ${formatDistance(point.distanceMetres)}`,
    })
      .addTo(map as L.Map)
      .bindTooltip(`${point.name}<br>${formatDistance(point.distanceMetres)}`, { direction: 'top' })
      .on('click', () => emit('select', index));

    markers.push(marker);
  });

  fit();
}

function fit() {
  const framed = (props.focus.length > 0 ? props.focus : props.points).filter(
    (point) => Number.isFinite(point.lat) && Number.isFinite(point.lng),
  );
  if (!map || framed.length === 0) {
    return;
  }

  map.fitBounds(L.latLngBounds(framed.map((point) => [point.lat, point.lng] as [number, number])), {
    padding: [30, 30],
    maxZoom: 15,
  });
}

function highlight(selected: number | null) {
  markers.forEach((marker, index) => {
    marker.setIcon(icon(index === selected));
    // Leaflet leaves a tooltip open until it is told otherwise, so without this every
    // location that was ever selected keeps its label and the map fills up with them.
    if (index !== selected) {
      marker.closeTooltip();
    }
  });

  if (selected !== null && markers[selected] && map) {
    map.panTo(markers[selected].getLatLng(), { animate: true });
    markers[selected].openTooltip();
  }
}

onMounted(() => {
  if (!container.value) {
    return;
  }

  map = L.map(container.value, {
    // A map that eats page scroll is worse than one that needs a click first.
    scrollWheelZoom: false,
    zoomControl: true,
  });

  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  }).addTo(map);

  draw();
  highlight(props.selected);
});

onBeforeUnmount(() => {
  map?.remove();
  map = null;
  markers = [];
});

watch(() => props.points, draw);
watch(() => props.focus, fit);
watch(() => props.selected, highlight);
</script>

<template>
  <div ref="container" class="pickup-map" role="application" aria-label="Kaart met ophaalpunten"></div>
</template>

<style>
/* Unscoped: Leaflet builds the marker element from an HTML string, so a scoped
   attribute never lands on it. */
.pickup-pin {
  display: block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--accent);
  border: 2px solid var(--surface);
  box-shadow: 0 1px 3px rgb(0 0 0 / 35%);
}

.pickup-pin--active {
  width: 20px;
  height: 20px;
  margin: -3px 0 0 -3px;
  background: var(--danger);
}

.leaflet-container {
  background: var(--surface-muted);
  font: inherit;
}
</style>

<style scoped>
.pickup-map {
  height: 240px;
  border-radius: 10px;
  border: 1px solid var(--border);
  overflow: hidden;
  z-index: 0;
}
</style>
